import React, { useState } from 'react';
import { Icon } from '../../Icon';
import { parseBrazilianNumber } from '../../../utils/formatUtils';
import { FormField, ResultCard, EducationalBlock } from './shared';

const InvestmentCalculator = ({ onResultChange }: { onResultChange: (data: { label: string, value: string }[]) => void }) => {
  const [ini, setIni] = useState('1.000');
  const [mensal, setMensal] = useState('500');
  const [taxa, setTaxa] = useState('11,75');
  const [tempo, setTempo] = useState('60');
  const [result, setResult] = useState<any>(null);

  const calculate = () => {
    const p = parseFloat(parseBrazilianNumber(ini)) || 0;
    const pmt = parseFloat(parseBrazilianNumber(mensal)) || 0;
    const rAnual = parseFloat(parseBrazilianNumber(taxa)) || 0;
    const n = parseInt(tempo) || 0;
    if (n <= 0) return;

    const rMensal = Math.pow(1 + rAnual / 100, 1 / 12) - 1; // Taxa equivalente mensal
    let totalInvestido = p;
    let montante = p;
    const evolucao = [];

    for (let i = 1; i <= n; i++) {
      const rendimento = montante * rMensal;
      montante = montante + rendimento + pmt;
      totalInvestido += pmt;
      if (i % (n > 24 ? 12 : 1) === 0 || i === n) {
        evolucao.push({ mes: i, total: montante, investido: totalInvestido, juros: montante - totalInvestido });
      }
    }

    const res = { montante, totalInvestido, totalJuros: montante - totalInvestido, evolucao, ini, mensal, taxa, tempo };
    setResult(res);

    onResultChange([
      { label: 'Investimento Inicial', value: `R$ ${p.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Aporte Mensal', value: `R$ ${pmt.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Taxa Anual', value: `${rAnual}%` },
      { label: 'Prazo', value: `${n} meses` },
      { label: 'Total Investido', value: `R$ ${totalInvestido.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Total em Juros', value: `R$ ${(montante - totalInvestido).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` },
      { label: 'Valor Final', value: `R$ ${montante.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` }
    ]);
  };

  return (
    <div className="space-y-8">
      <div className="bg-theme-result p-6 rounded-2xl border border-theme-border">
        <h2 className="text-xl font-bold text-theme-text mb-3">Calculadora de Investimentos</h2>
        <p className="text-theme-muted mb-4">
          Projeção de crescimento com aportes mensais e juros compostos.
          Planeje seu futuro financeiro com precisão.
        </p>
      </div>

      <div className="bg-theme-result p-8 rounded-3xl shadow-lg border border-theme-border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FormField
            label="Valor Inicial (R$)"
            placeholder="Ex: 1.000"
            value={ini}
            onChange={setIni}
            helpText="Aporte inicial"
            required
            isNumeric
          />

          <FormField
            label="Aporte Mensal (R$)"
            placeholder="Ex: 500"
            value={mensal}
            onChange={setMensal}
            helpText="Contribuição mensal"
            required
            isNumeric
          />

          <FormField
            label="Taxa Anual (%)"
            placeholder="Ex: 11,75"
            value={taxa}
            onChange={setTaxa}
            helpText="Rentabilidade anual"
            required
            isNumeric
          />

          <FormField
            label="Prazo (meses)"
            placeholder="Ex: 60"
            value={tempo}
            onChange={setTempo}
            helpText="Período de investimento"
            required
            type="text"
            isNumeric
          />
        </div>
        <div className="mt-8 flex justify-center">
          <button
            onClick={calculate}
            className="px-12 py-5 bg-accent-gradient text-white rounded-[2rem] font-black shadow-accent hover:shadow-accent-hover hover:scale-[1.02] transition-all hover:-translate-y-1 active:scale-95 uppercase text-sm tracking-widest flex items-center gap-3"
          >
            <Icon name="play" className="w-5 h-5" />
            Calcular Investimento
          </button>
        </div>
      </div>

      {result && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <ResultCard
              title="Valor Final"
              value={`R$ ${result.montante.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}`}
              description="Montante total"
              color="emerald"
            />

            <ResultCard
              title="Total Investido"
              value={`R$ ${result.totalInvestido.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}`}
              description="Seus aportes"
              color="blue"
            />

            <ResultCard
              title="Total em Juros"
              value={`R$ ${result.totalJuros.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}`}
              description="Rendimentos"
              color="purple"
            />

            <ResultCard
              title="Rentabilidade"
              value={`${result.totalInvestido > 0 ? ((result.totalJuros / result.totalInvestido) * 100).toFixed(1) : 0}%`}
              description="Retorno sobre investimento"
              color="amber"
            />
          </div>

          <div className="bg-theme-result rounded-3xl border border-theme-border overflow-hidden shadow-lg">
            <div className="px-6 py-4 bg-theme-input border-b border-theme-border">
              <div className="flex items-center justify-between">
                <span className="text-xs font-black text-theme-muted uppercase tracking-wider">Evolução</span>
                <span className="text-xs font-bold text-theme-muted bg-theme-muted-section px-3 py-1 rounded-full">{tempo} meses</span>
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              <table className="w-full text-left">
                <thead className="bg-theme-result sticky top-0 text-[11px] font-black text-theme-muted uppercase">
                  <tr>
                    <th className="px-6 py-4">Mês</th>
                    <th className="px-6 py-4">Investido</th>
                    <th className="px-6 py-4">Total</th>
                    <th className="px-6 py-4">Juros</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {result.evolucao.map((row: any) => (
                    <tr key={row.mes} className="hover:bg-theme-input transition-colors">
                      <td className="px-6 py-4 font-black text-theme-text">{row.mes}</td>
                      <td className="px-6 py-4 font-medium text-theme-muted">R$ {row.investido.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</td>
                      <td className="px-6 py-4 font-extrabold text-theme-text">R$ {row.total.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</td>
                      <td className="px-6 py-4 font-bold text-emerald-700">R$ {row.juros.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <EducationalBlock
            title="Dicas de Investimento"
            tips={[
              "Juros compostos são poderosos: comece a investir o mais cedo possível",
              "Diversifique seus investimentos para balancear risco e retorno",
              "Aportes mensais regulares potencializam o resultado final",
              "Defina metas realistas e acompanhe sua evolução patrimonial"
            ]}
            warnings={[
              "Rentabilidade passada não garante resultados futuros",
              "Considere inflação no poder de compra futuro",
              "Mantenha uma reserva de emergência separada das metas",
              "Investimentos envolvem riscos de perda"
            ]}
          />
        </>
      )}

      {!result && (
        <div className="h-full flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 rounded-[2.5rem] border-2 border-dashed border-theme-border text-theme-muted p-10 text-center">
          <div className="w-16 h-16 bg-slate-200 rounded-full flex items-center justify-center text-2xl mb-3">💰</div>
          <p className="font-bold text-base">Preencha os dados e clique em Calcular</p>
        </div>
      )}
    </div>
  );
};

export default InvestmentCalculator;
